import React, {useEffect, useRef, useState, useMemo} from 'react';
import {history} from '../../../../utils/redux/store';
import {privilegeUtils} from '../../../../utils/privilegeUtils';
import {useSelector} from 'react-redux';
import useSkincare from '../../../../components/hook/useSkincare';
import useSolutionType from '../../../../components/hook/useSolutionType';
import Header from '../../components/header';
import Loader from '../../../../components/spinner/Loader';
import {Trans} from 'react-i18next';
import {Form, Formik} from 'formik';
import ChooseUITemplate from '../components/ChooseUITemplate';
import CustomizedLayoutSettings from './components/CustomizedLayoutSettings';
import ButtonWithRestoreGroup from '../../../../components/formik/ButtonWithRestoreGroup';
import PublishToOnlineModal from '../components/PublishToOnlineModal';
import _get from 'lodash/get';
import skincareProDao from '../../../../dao/skincareProDao';
import {toast} from 'react-toastify';
import {
  deviceOptions,
  getAvailableDevices,
  setUiConfigDevice,
} from '../config/utils/optionsUtils';
import langCode from '../../../../utils/langUtils';
import {useQuery} from '../../../../components/hook/useQuery';
import {
  getTemplate,
  getTemplateLayout,
  getBrandId,
  treatmentTypes,
  combineTreatments,
} from '../../../../utils/skincareUtils';
import smbUtils from '../../../../utils/smbUtils';
import PreviewModule from '../../components/previewmodule';
import {handleGenericTemplateExport} from '../../../../utils/skincareUtils';
import ElevatedButton from '../../../../components/formik/ElevatedButton';
import OverLayLoader from '../../../../components/spinner/OverLayLoader';
import ConfigurationModal from '../components/ConfigurationModal';

export default function SharedSimulationReportPage() {
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {token, customerId, brandInfo} = smbUtils.customerInfo(auth);
  const {isV2Above, isVersionAbove2AndHd} = useSkincare(auth);
  const {isSimulationPremium} = useSolutionType(auth);
  const formRef = useRef();
  const isTechnicalSupport = privilegeUtils.isTechnicalSupport(auth);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [initValue, setInitValue] = useState({});
  const [commonData, setCommonData] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [customTempDataset, setCustomTempDataset] = useState([]);
  const [generalTempInfo, setGeneralTempInfo] = useState({});
  const [selectedTempInfo, setSelectedTempInfo] = useState({});
  const [language, setLanguage] = useState(langCode.getInitialLang());
  const [treatments, setTreatments] = useState([]);
  const [
    customSimulationTreatmentData,
    setCustomSimulationTreatmentData,
  ] = useState({});

  const [publishStatus, setPublishStatus] = useState(false);
  const [isShowPublishModal, setIsShowPublishModal] = useState(false);
  const [modalState, setModalState] = useState({
    isShow: false,
    type: null,
    showFooter: false,
  });
  const templateType = 'SmbSimulationSharePage';
  const templateType2 = 'SmbSimulationResultPage';
  const templateType3 = 'simulationResult';

  const {
    data: templateUrlData,
    isLoading: isLoadingByTemplateUrl,
    refetch: tempPreviewUrlRefetch,
  } = useQuery({
    queryFunc: params => {
      if (!_get(params, 'rvId')) {
        return;
      }
      return _get(params, 'isDefault')
        ? getTemplateLayout(
            token,
            customerId,
            templateType,
            _get(brandInfo, 'templateVersion')
          )
        : getTemplate(token, customerId, _get(params, 'rvId'));
    },
    initData: {
      indexPage: '',
      indexFileUrl: '',
    },
    isInitEnabled: false,
  });

  const updateCustomLang = availableLangs => {
    const availableLangList = langCode.getConsoleStringLangList();
    const matchedLang = availableLangList.find(lang =>
      availableLangs.includes(lang.value)
    );
    setLanguage(matchedLang || availableLangList[0]);
  };

  const fetchData = async () => {
    setIsInitialLoading(true);
    try {
      const promises = [
        skincareProDao.listUIConfigTemplates({template_type: templateType2}),
      ];

      if (isVersionAbove2AndHd) {
        promises.push(
          skincareProDao.getCustomerSetting({type: 'ScpAppSettings'})
        );
      }

      const results = await Promise.all(
        promises.map(promise => promise.catch(() => ({})))
      );

      const templateLists = results[0];
      const appSettingsConfig = isVersionAbove2AndHd ? results[1] : null;

      if (isVersionAbove2AndHd && appSettingsConfig) {
        const customTreatmentName = _get(
          appSettingsConfig,
          'editing_revision.data.custom_simulation_treatment_name',
          {}
        );
        setCustomSimulationTreatmentData(customTreatmentName);
      }

      const templates = _get(templateLists, 'templates', []);
      setCustomTempDataset(templates.filter(template => !template.is_default));

      const defaultItem = templates.find(t => t.is_default);
      if (!defaultItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }
      setGeneralTempInfo(defaultItem);

      const res = await getTemplate(
        token,
        customerId,
        _get(defaultItem, 'template_revision_id')
      );

      if (res && res.configJson) {
        const parsedConfig = JSON.parse(res.configJson);
        const pages = _get(parsedConfig, 'page', []);
        const resultPage =
          pages.find(p => p.pageType === 'simulationResult') || pages[0];

        setInitValue({
          page: resultPage ? [resultPage] : [],
          is_default: _get(res, 'is_default', true),
          template_id: _get(res, 'templateId', ''),
          template_revision_id: _get(defaultItem, 'template_revision_id', null),
        });
      }

      const brandData = await getBrandId(token, customerId);
      if (brandData && _get(brandData, 'commonSettingRvId')) {
        await getTemplate(
          token,
          customerId,
          _get(brandData, 'commonSettingRvId'),
          'CommonSetting'
        ).then(r => {
          if (r && r.configJson) {
            setCommonData(JSON.parse(r.configJson));
          }
        });
      }

      if (brandData && _get(brandData, 'facialTreatmentRvId')) {
        await getTemplate(
          token,
          customerId,
          _get(brandData, 'facialTreatmentRvId')
        ).then(res => {
          if (res && res.metaDataJson) {
            const facialZones =
              JSON.parse(_get(res, 'metaDataJson', '{}')).facialZones || [];
            const treatmentOptions =
              JSON.parse(_get(res, 'metaDataJson', '{}')).treatmentOptions ||
              [];
            if (facialZones.length > 0) {
              setTreatments(
                combineTreatments(
                  treatmentOptions,
                  isSimulationPremium,
                  facialZones
                )
              );
            }
          }
        });
      }

      const selectedItem = templates.find(t => t.selected);
      if (!selectedItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }

      if (selectedItem.is_default) {
        setSelectedTempInfo({
          ...selectedItem,
          device_type: deviceOptions.map(opt => opt.value),
          langs: langCode.getConsoleStringLangList().map(opt => opt.value),
        });
      } else {
        setSelectedTempInfo(selectedItem);
        updateCustomLang(_get(selectedItem, 'langs', []));
      }

      await tempPreviewUrlRefetch({
        isDefault: selectedItem.is_default,
        rvId: selectedItem.template_revision_id,
      });
    } catch (error) {
      toast.error(
        <Trans i18nKey="toast.error.loading.data.failed">
          Failed to load data.
        </Trans>
      );
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSelectedTemplate = async (formikProps, targetItem) => {
    formikProps.setFieldValue(
      'is_default',
      _get(targetItem, 'is_default', true)
    );
    formikProps.setFieldValue(
      'template_id',
      _get(targetItem, 'template_id', null)
    );
    formikProps.setFieldValue(
      'template_revision_id',
      _get(targetItem, 'template_revision_id', null)
    );
    setSelectedTempInfo(targetItem);
    updateCustomLang(_get(targetItem, 'langs', []));
    await tempPreviewUrlRefetch({
      isDefault: targetItem.is_default,
      rvId: targetItem.template_revision_id,
    });
  };

  const handleSubmit = async targetItem => {
    try {
      const r = await skincareProDao.selectUIConfigTemplate({
        ...targetItem,
        template_type: templateType,
      });
      if (r && r.status === 'success') {
        setPublishStatus(!publishStatus);
        setIsShowPublishModal(true);
        await fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleExport = async templateId => {
    await handleGenericTemplateExport({
      templateId,
      fileName: 'SharedSimulationReportPage_Generic',
      setIsExporting,
      setModalState,
    });
  };

  const formikConfig = {
    enableReinitialize: true,
    initialValues: (() => {
      const selectedItem = customTempDataset.find(item => item.selected) || {};
      return {
        is_default: _get(selectedItem, 'is_default', true),
        template_id: _get(selectedItem, 'template_id', null),
        template_revision_id: _get(selectedItem, 'template_revision_id', null),
      };
    })(),
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);
      if (
        !privilegeUtils.canCustomizationAppLayout(auth) &&
        !values.is_default
      ) {
        toast.error(
          <Trans i18nKey="toast.error.update.version.failed">
            Failed to update, please try again.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default && !values.template_id) {
        toast.error(
          <Trans i18nKey="toast.error.update.template.failed">
            Failed to update, please choose a template.
          </Trans>
        );
        setSubmitting(false);
        return;
      }
      await handleSubmit(values);
      setSubmitting(false);
    },
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (
    !(
      privilegeUtils.canUiConfig(auth) ||
      privilegeUtils.isTechnicalSupport(auth) ||
      !isV2Above
    )
  ) {
    history.push('/');
  }

  const resultPage = useMemo(() => {
    if (!initValue || !initValue.page) return null;
    const page =
      initValue.page.find(p => p.pageType === 'simulationResult') ||
      initValue.page[0];

    return {
      ...page,
      reportBanner: {
        ...page.reportBanner,
        mainImage: [
          {
            ..._get(page, 'reportBanner.mainImage[0]', {}),
            imageFile: _get(page, 'reportBanner.mainImage[0].imageUrl', ''),
          },
          {
            ..._get(page, 'reportBanner.mainImage[1]', {}),
            imageFile: _get(page, 'reportBanner.mainImage[1].imageUrl', ''),
          },
          _get(page, 'reportBanner.mainImage[2]')
            ? {
                ..._get(page, 'reportBanner.mainImage[2]'),
                imageFile: _get(
                  page,
                  'reportBanner.mainImage[2].imageUrl'
                ),
              }
            : {
                type: 'padLandscape',
                image:
                  './assets/images/img_simulation_report_banner_landscape.jpg',
                imageUrl:
                  'https://app-cdn-01.perfectcorp.com/console-demo/common/misc/template_default_config/image/img_simulation_report_banner_landscape.jpg',
              },
        ],
      },
      smb: {
        treatmentImages: {
          ...treatmentTypes.reduce((data, current) => {
            data[current.value] = {
              image: _get(
                page,
                `smb.treatmentImages.${current.value}.image`
              ),
              imageUrl: _get(
                page,
                `smb.treatmentImages.${current.value}.imageUrl`
              ),
              imageFile: _get(
                page,
                `smb.treatmentImages.${current.value}.imageUrl`
              ),
            };
            return data;
          }, {}),
        },
      },
    };
  }, [initValue]);

  return isInitialLoading ? (
    <div>
      <Header />
      <Loader />
    </div>
  ) : (
    <>
      <Formik {...formikConfig} ref={formRef}>
        {formikProps => (
          <Form className="skincare-configuration-form">
            <Header publishStatus={publishStatus} dirty={formikProps.dirty} />
            <div className="skincare-configuration-layout">
              <div className="skincare-configuration-layout__left">
                <div className="kt-portlet skincare-configuration-layout__left-portlet">
                  <div className="kt-portlet__body pb-0">
                    <ChooseUITemplate
                      isDefaultLayout={_get(selectedTempInfo, 'is_default')}
                      setIsDefaultLayout={v => {
                        let targetItem = customTempDataset.find(
                          item => item.is_default === false
                        );
                        if (!targetItem) {
                          targetItem = {
                            is_default: v,
                            template_id: null,
                            template_revision_id: null,
                            device_type: deviceOptions.map(op => op.value),
                            langs: langCode
                              .getConsoleStringLangList()
                              .map(op => op.value),
                          };
                        }
                        if (v) {
                          targetItem = {
                            ...generalTempInfo,
                            is_default: true,
                            device_type: deviceOptions.map(op => op.value),
                            langs: langCode
                              .getConsoleStringLangList()
                              .map(op => op.value),
                          };
                        }
                        handleSelectedTemplate(formikProps, targetItem);
                      }}
                    />
                    <div
                      className="kt-margin-t-10 kt-margin-b-10"
                      style={{
                        backgroundColor: '#e1e1ef',
                        height: '1px',
                      }}
                    />
                  </div>
                  <div className="kt-portlet__body skincare-configuration-layout__left-portlet__body">
                    {_get(selectedTempInfo, 'is_default') && (
                      <div>
                        <p>
                          <Trans i18nKey="p.s.uiconfiguration.sharedsimulationreportpage.generic.ui.desc">
                            This page’s generic UI uses the same configuration
                            as the Simulation Report Page. Separate
                            configuration is not supported.
                          </Trans>
                        </p>
                      </div>
                    )}
                    {!_get(selectedTempInfo, 'is_default') && (
                      <CustomizedLayoutSettings
                        data={customTempDataset}
                        isLoading={isInitialLoading}
                        selectedTempInfo={selectedTempInfo}
                        handleSelectedTemplate={targetItem =>
                          handleSelectedTemplate(formikProps, targetItem)
                        }
                        isTechnicalSupport={isTechnicalSupport}
                      />
                    )}
                    <div className="d-flex justify-content-between kt-margin-t-20 align-items-center">
                      {isTechnicalSupport &&
                      _get(selectedTempInfo, 'is_default') ? (
                        <div className="kt-margin-b-10">
                          <ElevatedButton
                            type="custom"
                            faIcon="fa-download"
                            text={
                              <Trans i18nKey="p.s.uiconfiguration.export.current.ui.html.file">
                                Export Current UI As HTML File
                              </Trans>
                            }
                            onClick={() =>
                              handleExport(selectedTempInfo.template_id)
                            }
                            disabled={isExporting}
                          />
                        </div>
                      ) : (
                        <div />
                      )}
                      {privilegeUtils.canCustomizationAppLayout(auth) && (
                        <div>
                          <ButtonWithRestoreGroup
                            saveDirty={formikProps.dirty}
                            discardButton={false}
                            isUpdating={true}
                            onRestoreClick={() => {
                              handleSubmit(generalTempInfo);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {!isLoadingByTemplateUrl &&
                initValue &&
                initValue.page &&
                resultPage &&
                treatments.length > 0 && (
                  <div className="skincare-configuration-layout__right">
                    <PreviewModule
                      values={formikProps.values}
                      template={{
                        ...(_get(selectedTempInfo, 'is_default') && {
                          page: {
                            page: [
                              {
                                ...resultPage,
                                preview: {
                                  treatmentOrder: treatments,
                                  customName: {
                                    custom_simulation_treatment_name: customSimulationTreatmentData,
                                  },
                                },
                              },
                            ],
                          },
                        }),
                        common: {
                          ...commonData,
                          page: [
                            {
                              ..._get(commonData, 'page[0]'),
                              header: {
                                logo: {
                                  image: _get(
                                    commonData,
                                    'page[0].header.logo.imageUrl',
                                    ''
                                  ),
                                },
                              },
                            },
                          ],
                        },
                      }}
                      previewUrl={
                        selectedTempInfo.is_default
                          ? _get(templateUrlData, 'indexPage')
                          : _get(templateUrlData, 'indexFileUrl')
                      }
                      lang={language}
                      type="SimulationPage"
                      deviceList={getAvailableDevices(
                        deviceOptions,
                        _get(selectedTempInfo, 'device_type', [])
                      )}
                      langList={langCode.getAvailableConsoleStringLangList(
                        _get(selectedTempInfo, 'langs', [])
                      )}
                      onChangeDevice={device =>
                        setUiConfigDevice(device, customerId)
                      }
                      isCustomized={!_get(selectedTempInfo, 'is_default', false)}
                    />
                  </div>
                )}
            </div>
            <PublishToOnlineModal
              isShow={isShowPublishModal}
              onPublish={isPublish => {
                formikProps.setSubmitting(false);
                formikProps.resetForm(formikProps.values);
                setPublishStatus(isPublish);
                setIsShowPublishModal(false);
              }}
            />
            <ConfigurationModal
              modalState={{
                ...modalState,
                customDesc:
                  modalState.type === 'outdatedConfig' ? (
                    <Trans i18nKey="p.s.uiconfiguration.outdated.config.shared.simulation.report.desc">
                      To export your UI as HTML, you'll need to upgrade your
                      config version. Just go to configuration of{' '}
                      <a
                        href="javascript:;"
                        onClick={() => {
                          history.push(
                            '/skincare-pro/ui-configuration/simulation-report'
                          );
                        }}
                        style={{textDecoration: 'underline'}}
                      >
                        Simulation Report Page & Get Simulation Report Page
                      </a>{' '}
                      and click <b>Save</b> to update — no edits to your current
                      settings are required.
                    </Trans>
                  ) : (
                    undefined
                  ),
              }}
              onClose={() => setModalState({isShow: false, type: null})}
            />
          </Form>
        )}
      </Formik>
      {isExporting && (
        <OverLayLoader
          text={
            <Trans i18nKey="c.spinner.overlay.loader.exporting">
              Exporting...
            </Trans>
          }
        />
      )}
    </>
  );
}
